import { User, UserRole } from '../graphql/codegen/graphql-react';
import { ls } from '../i18n/translations';

/** Primary role enum values in stable display order for grouped lists. */
export const USER_ROLE_GROUP_ORDER: UserRole[] = [
  UserRole.Admin,
  UserRole.Admin2,
  UserRole.Supertechnician,
  UserRole.Technician,
  UserRole.Viewer,
];

const USER_ROLE_VALUE_SET = new Set<string>(Object.values(UserRole));

/** Maps API/cache strings to UserRole so indexOf matches even with loose typing. */
const coerceToUserRole = (value: unknown): UserRole | null => {
  if (value == null || value === '') return null;
  const normalized = String(value).trim().toUpperCase();
  if (!USER_ROLE_VALUE_SET.has(normalized)) return null;
  return normalized as UserRole;
};

/**
 * Picks the highest-privilege role when the API returns multiple roles in arbitrary order.
 * Uses optional `roleId` when `roles` is empty or values fail to parse (some backends only set roleId).
 */
export const getPrimaryUserRole = (
  roles?: (UserRole | null)[] | null,
  roleIdFallback?: string | null,
): UserRole => {
  const candidates: unknown[] = [...(roles || [])];
  if (roleIdFallback) {
    candidates.push(roleIdFallback);
  }
  if (candidates.length === 0) return UserRole.Viewer;
  let best: UserRole | null = null;
  let bestIdx = Infinity;
  for (const r of candidates) {
    const coerced = coerceToUserRole(r);
    if (coerced == null) continue;
    const idx = USER_ROLE_GROUP_ORDER.indexOf(coerced);
    if (idx === -1) continue;
    if (idx < bestIdx) {
      bestIdx = idx;
      best = coerced;
    }
  }
  return best ?? UserRole.Viewer;
};

export const getRoleLabel = (
  roles?: (UserRole | null)[] | null,
  roleIdFallback?: string | null,
): string => {
  if ((!roles || roles.length === 0) && !roleIdFallback) return '';
  const role = getPrimaryUserRole(roles, roleIdFallback);
  switch (role) {
    case UserRole.Admin:
      return ls.admin;
    case UserRole.Admin2:
      return ls.admin2;
    case UserRole.Supertechnician:
      return ls.superTechnician;
    case UserRole.Technician:
      return ls.technician;
    case UserRole.Viewer:
      return ls.viewer;
    default:
      return role ?? '';
  }
};

export const dedupeUsersById = (list: User[]): User[] => {
  const byId = new Map<string, User>();
  for (const u of list) {
    if (u.id && !byId.has(u.id)) {
      byId.set(u.id, u);
    }
  }
  return Array.from(byId.values());
};

/**
 * Direct users created by `parent`, excluding ids in `ancestorIds` (cycle guard — same rules as UserOrgChart).
 */
export const getDirectChildUsers = (parent: User, allUsers: User[], ancestorIds: Set<string>): User[] => {
  const user = parent;
  return allUsers.filter((u) => {
    if (!u.id || u.createdById !== user.id) return false;
    if (u.id === user.id) return false;
    if (ancestorIds.has(u.id)) return false;
    return true;
  });
};

export const getUserDisplayName = (u: User): string => {
  const full = `${u.name || ''} ${u.surname || ''}`.trim();
  return full || u.email || '—';
};

/**
 * True if targetId equals ancestorId or lies in the subtree below ancestorId (createdById edges).
 */
export const isTargetInDescendantTree = (ancestorId: string, targetId: string, allUsers: User[]): boolean => {
  if (ancestorId === targetId) return true;
  const queue: string[] = [ancestorId];
  const seen = new Set<string>();
  while (queue.length) {
    const id = queue.shift()!;
    if (seen.has(id)) continue;
    seen.add(id);
    for (const c of allUsers) {
      if (!c.id || c.createdById !== id) continue;
      if (c.id === targetId) return true;
      queue.push(c.id);
    }
  }
  return false;
};

export const sortRoleKeys = (keys: string[]): string[] => {
  return [...keys].sort((a, b) => {
    const ia = USER_ROLE_GROUP_ORDER.indexOf(a as UserRole);
    const ib = USER_ROLE_GROUP_ORDER.indexOf(b as UserRole);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
};

/**
 * Users reachable from natural roots by following creator → created edges only.
 */
const getReachableUserIds = (naturalRoots: User[], allUsers: User[]): Set<string> => {
  const reachable = new Set<string>();
  const queue = [...naturalRoots];
  while (queue.length) {
    const u = queue.shift()!;
    if (!u.id || reachable.has(u.id)) continue;
    reachable.add(u.id);
    for (const c of allUsers) {
      if (c.id && c.createdById === u.id && c.id !== u.id && !reachable.has(c.id)) {
        queue.push(c);
      }
    }
  }
  return reachable;
};

/**
 * Users not reachable from main roots (separate branches, cycles, etc.), split into components.
 */
const partitionStrandedUsers = (stranded: User[], allUsers: User[]): User[][] => {
  if (stranded.length === 0) return [];
  const strandedIds = new Set(stranded.map((u) => u.id!));
  const byId = new Map<string, User>();
  allUsers.forEach((u) => {
    if (u.id) byId.set(u.id, u);
  });
  const remaining = new Set(stranded.map((u) => u.id!));
  const components: User[][] = [];

  while (remaining.size) {
    const startId = remaining.values().next().value as string;
    const comp: User[] = [];
    const q = [byId.get(startId)!];
    while (q.length) {
      const x = q.shift()!;
      if (!x?.id || !remaining.has(x.id)) continue;
      remaining.delete(x.id);
      comp.push(x);
      for (const c of allUsers) {
        if (c.createdById === x.id && c.id && remaining.has(c.id)) {
          q.push(c);
        }
      }
      const pid = x.createdById;
      if (pid && strandedIds.has(pid) && remaining.has(pid)) {
        const p = byId.get(pid);
        if (p) q.push(p);
      }
    }
    components.push(comp);
  }
  return components;
};

/**
 * Root users for the creator hierarchy — same rules as UserOrgChart `buildOrgTree`.
 * Call with a deduped list (use `dedupeUsersById` first).
 */
export const getHierarchyRootUsers = (usersDeduped: User[]): User[] => {
  if (usersDeduped.length === 0) return [];

  const userMap = new Map<string, User>();
  usersDeduped.forEach((u) => {
    if (u.id) userMap.set(u.id, u);
  });

  const naturalRoots = usersDeduped.filter((u) => !u.createdById || !userMap.has(u.createdById));
  const reachableIds = getReachableUserIds(naturalRoots, usersDeduped);
  const stranded = usersDeduped.filter((u) => u.id && !reachableIds.has(u.id));
  const strandedRoots = partitionStrandedUsers(stranded, usersDeduped).map((comp) =>
    comp.slice().sort((a, b) => (a.id || '').localeCompare(b.id || ''))[0],
  );

  const mergedRoots: User[] = [...naturalRoots];
  for (const sr of strandedRoots) {
    if (sr && !mergedRoots.some((r) => r.id === sr.id)) {
      mergedRoots.push(sr);
    }
  }

  const allRoots = Array.from(
    new Map(mergedRoots.filter((r) => r?.id).map((r) => [r.id!, r])).values(),
  );

  if (allRoots.length === 0) {
    const withId = usersDeduped.filter((u) => u.id);
    if (withId.length === 0) return [];
    return [withId[0]];
  }

  return allRoots.sort((a, b) => (a.id || '').localeCompare(b.id || ''));
};
